import React from 'react';
import SectionCard from '../controls/SectionCard';
import Row from '../controls/Row';
import Segmented from '../controls/Segmented';
import Slider from '../controls/Slider';
import Toggle from '../controls/Toggle';
import NumberInput from '../controls/NumberInput';
import ColorPanel from '../../ColorPanel/ColorPanel';
import { DEFAULT_PAGE_SETTINGS } from '../../../utils/pageSettings';
import { normalizePaint } from '../../../utils/paintUtils';
import styles from './PageSettingsSection.module.css';

const PageSettingsSection = ({
  pageSettings,
  onUpdate,
  onPreviewUpdate,
  brandColors,
  palette,
  onUsePaint,
  onRequestEyedropper,
  assets,
  assetsLoading,
  onSelectBackgroundImage,
  onClearBackgroundImage,
  onToggleBackgroundReposition,
  isBackgroundRepositioning,
  onCommitBackgroundReposition,
  onCancelBackgroundReposition,
  onResetBackgroundReposition,
  artboardWidth,
  artboardHeight,
}) => {
  const settings = pageSettings || DEFAULT_PAGE_SETTINGS;
  const backgroundPaint = normalizePaint(
    settings.backgroundPaint || settings.background || DEFAULT_PAGE_SETTINGS.backgroundPaint,
    '#FFFFFF',
  );
  const backgroundImage = settings.backgroundImage || DEFAULT_PAGE_SETTINGS.backgroundImage;
  const imageEnabled = Boolean(backgroundImage?.enabled && backgroundImage?.src);
  const [imagePickerOpen, setImagePickerOpen] = React.useState(false);

  const applyUpdate = (patch) => {
    if (onPreviewUpdate) {
      onPreviewUpdate(patch);
    } else {
      onUpdate(patch);
    }
  };

  const updateBackgroundPaint = (paint) => {
    onUpdate({ backgroundPaint: normalizePaint(paint, '#FFFFFF') });
  };

  const updateBackgroundAlpha = (alpha) => {
    const normalized = normalizePaint(backgroundPaint, '#FFFFFF');
    if (normalized.type === 'solid') {
      updateBackgroundPaint({ ...normalized, alpha });
      return;
    }
    updateBackgroundPaint({
      ...normalized,
      stops: normalized.stops.map((stop) => ({ ...stop, alpha })),
    });
  };

  const updateBackgroundImage = (patch, commit = false) => {
    const next = {
      ...(backgroundImage || DEFAULT_PAGE_SETTINGS.backgroundImage),
      ...patch,
    };
    if (commit || !onPreviewUpdate) {
      onUpdate({ backgroundImage: next });
    } else {
      onPreviewUpdate({ backgroundImage: next });
    }
  };

  const imageAssets = React.useMemo(
    () => (assets || []).filter((asset) => asset.type !== 'pdf'),
    [assets],
  );

  const coverScale = React.useMemo(() => {
    if (!imageEnabled) return 1;
    const naturalW = backgroundImage.naturalW || 1;
    const naturalH = backgroundImage.naturalH || 1;
    const targetW = artboardWidth || naturalW;
    const targetH = artboardHeight || naturalH;
    return Math.max(targetW / naturalW, targetH / naturalH);
  }, [artboardWidth, artboardHeight, backgroundImage.naturalH, backgroundImage.naturalW, imageEnabled]);

  return (
    <>
      <SectionCard title="Background">
        <Row label="Colour">
          <ColorPanel
            label="Background"
            value={backgroundPaint}
            allowGradient
            documentColors={palette?.documentColors}
            recentColors={palette?.recentColors}
            recentGradients={palette?.recentGradients}
            brandColors={brandColors}
            onChange={updateBackgroundPaint}
            onCommit={updateBackgroundPaint}
            onUsePaint={onUsePaint}
            onRequestEyedropper={
              onRequestEyedropper
                ? () => onRequestEyedropper((paint) => updateBackgroundPaint(paint))
                : undefined
            }
          />
        </Row>
        <Row label="Opacity">
          <Slider
            min={0}
            max={100}
            step={1}
            value={backgroundPaint.alpha ?? 100}
            onChange={(value) => updateBackgroundAlpha(value)}
            onCommit={() => updateBackgroundAlpha(backgroundPaint.alpha ?? 100)}
          />
        </Row>
      </SectionCard>

      <SectionCard title="Background image">
        <Row label="Image">
          <div className={styles.imageActions}>
            <button
              type="button"
              className={styles.imageActionButton}
              onClick={() => setImagePickerOpen((prev) => !prev)}
            >
              {imageEnabled ? 'Replace' : 'Choose'}
            </button>
            {imageEnabled && (
              <>
                {!isBackgroundRepositioning ? (
                  <>
                    <button
                      type="button"
                      className={styles.imageActionButton}
                      onClick={onToggleBackgroundReposition}
                    >
                      Reposition
                    </button>
                    <button
                      type="button"
                      className={styles.imageActionButtonGhost}
                      onClick={() => onClearBackgroundImage?.()}
                    >
                      Remove
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className={styles.imageActionButton}
                      onClick={onCommitBackgroundReposition}
                    >
                      Done
                    </button>
                    <button
                      type="button"
                      className={styles.imageActionButtonGhost}
                      onClick={onCancelBackgroundReposition}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={styles.imageActionButtonGhost}
                      onClick={onResetBackgroundReposition}
                    >
                      Reset
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </Row>
        {imageEnabled && (
          <>
            <Row label="Opacity">
              <Slider
                min={0}
                max={100}
                step={1}
                value={backgroundImage.opacity ?? 100}
                onChange={(value) => updateBackgroundImage({ opacity: value })}
                onCommit={() => updateBackgroundImage({ opacity: backgroundImage.opacity ?? 100 }, true)}
              />
            </Row>
            <Row label="Zoom">
              <Slider
                min={coverScale}
                max={Math.max(coverScale * 5, coverScale + 0.1)}
                step={0.01}
                value={backgroundImage.scale ?? coverScale}
                onChange={(value) => updateBackgroundImage({ scale: value })}
                onCommit={() => updateBackgroundImage({ scale: backgroundImage.scale ?? coverScale }, true)}
              />
            </Row>
            <div className={styles.imageHint}>
              {isBackgroundRepositioning
                ? 'Drag on the canvas to reposition the background image. Scroll to zoom.'
                : 'Click Reposition to move the background image.'}
            </div>
          </>
        )}
        {imagePickerOpen && (
          <div className={styles.imagePicker}>
            <div className={styles.imagePickerHeader}>
              <span>Choose an image</span>
              <button
                type="button"
                className={styles.imagePickerClose}
                onClick={() => setImagePickerOpen(false)}
              >
                ×
              </button>
            </div>
            {assetsLoading ? (
              <div className={styles.imagePickerEmpty}>Loading uploads...</div>
            ) : imageAssets.length === 0 ? (
              <div className={styles.imagePickerEmpty}>No uploads yet.</div>
            ) : (
              <div className={styles.imagePickerGrid}>
                {imageAssets.slice(0, 12).map((asset) => (
                  <button
                    type="button"
                    key={asset.id}
                    className={styles.imagePickerTile}
                    onClick={() => {
                      onSelectBackgroundImage?.(asset);
                      setImagePickerOpen(false);
                    }}
                  >
                    <img
                      src={asset.thumbUrl || asset.downloadUrl}
                      alt={asset.originalName || 'Upload'}
                      className={styles.imagePickerThumb}
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </SectionCard>

      <SectionCard title="Guides & Grid">
        <Row label="Rulers">
          <Toggle
            checked={settings.showRulers}
            onChange={() => onUpdate({ showRulers: !settings.showRulers })}
            label={settings.showRulers ? 'On' : 'Off'}
          />
        </Row>
        <Row label="Guides">
          <Toggle
            checked={settings.showGuides}
            onChange={() => onUpdate({ showGuides: !settings.showGuides })}
            label={settings.showGuides ? 'On' : 'Off'}
          />
        </Row>
        <Row label="Margin">
          <Toggle
            checked={settings.showMargin}
            onChange={() => onUpdate({ showMargin: !settings.showMargin })}
            label={settings.showMargin ? 'On' : 'Off'}
          />
          <NumberInput
            value={settings.margin}
            min={0}
            max={500}
            onCommit={(value) => onUpdate({ margin: value })}
            suffix="px"
          />
        </Row>
        <Row label="Bleed">
          <Toggle
            checked={settings.showBleed}
            onChange={() => onUpdate({ showBleed: !settings.showBleed })}
            label={settings.showBleed ? 'On' : 'Off'}
          />
          <NumberInput
            value={settings.bleed}
            min={0}
            max={500}
            onCommit={(value) => onUpdate({ bleed: value })}
            suffix="px"
          />
        </Row>
        <Row label="Grid">
          <Toggle
            checked={settings.showGrid}
            onChange={() => onUpdate({ showGrid: !settings.showGrid })}
            label={settings.showGrid ? 'On' : 'Off'}
          />
        </Row>
        {settings.showGrid && (
          <>
            <Row label="Grid type">
              <Segmented
                value={settings.grid?.type || 'dots'}
                options={[
                  { value: 'dots', label: 'Dots' },
                  { value: 'lines', label: 'Lines' },
                ]}
                onChange={(value) => onUpdate({ grid: { ...settings.grid, type: value } })}
              />
            </Row>
            <Row label="Grid size">
              <NumberInput
                value={settings.grid?.size || 50}
                min={8}
                max={400}
                onCommit={(value) => onUpdate({ grid: { ...settings.grid, size: value } })}
                suffix="px"
              />
            </Row>
            <Row label="Grid opacity">
              <Slider
                min={0.05}
                max={0.8}
                step={0.01}
                value={settings.grid?.opacity || 0.2}
                onChange={(value) => onUpdate({ grid: { ...settings.grid, opacity: value } })}
                onCommit={() => onUpdate({ grid: { ...settings.grid } })}
                showValue
              />
            </Row>
          </>
        )}
      </SectionCard>

      <SectionCard title="Snapping">
        <Row label="Enable snap">
          <Toggle
            checked={settings.snap?.enabled}
            onChange={() => onUpdate({ snap: { ...settings.snap, enabled: !settings.snap?.enabled } })}
            label={settings.snap?.enabled ? 'On' : 'Off'}
          />
        </Row>
        <Row label="Threshold">
          <NumberInput
            value={settings.snap?.threshold ?? 6}
            min={2}
            max={20}
            onCommit={(value) => onUpdate({ snap: { ...settings.snap, threshold: value } })}
            suffix="px"
          />
        </Row>
        <Row label="Snap to">
          <div className={styles.snapGrid}>
            <Toggle
              checked={settings.snap?.toGrid}
              onChange={() => onUpdate({ snap: { ...settings.snap, toGrid: !settings.snap?.toGrid } })}
              label="Grid"
            />
            <Toggle
              checked={settings.snap?.toGuides}
              onChange={() => onUpdate({ snap: { ...settings.snap, toGuides: !settings.snap?.toGuides } })}
              label="Guides"
            />
            <Toggle
              checked={settings.snap?.toCenter}
              onChange={() => onUpdate({ snap: { ...settings.snap, toCenter: !settings.snap?.toCenter } })}
              label="Center"
            />
            <Toggle
              checked={settings.snap?.toEdges}
              onChange={() => onUpdate({ snap: { ...settings.snap, toEdges: !settings.snap?.toEdges } })}
              label="Edges"
            />
            <Toggle
              checked={settings.snap?.toMargin}
              onChange={() => onUpdate({ snap: { ...settings.snap, toMargin: !settings.snap?.toMargin } })}
              label="Margin"
            />
          </div>
        </Row>
      </SectionCard>
    </>
  );
};

export default PageSettingsSection;
